import { useEffect, useRef, useState } from "react";

import type { FollowUpSuggestionsResponse } from "../services/api";

interface SuggestionMessage {
  id: string | number;
  role: string;
  content?: string;
  isStreaming?: boolean;
  hubMessageId?: number;
  hubMessageIds?: number[];
}

interface UseFollowUpSuggestionChipsOptions {
  enabled: boolean;
  workspaceId: string;
  conversationId: string | number | null | undefined;
  messages: SuggestionMessage[];
  isLoading: boolean;
  fetchSuggestions: (
    workspaceId: string,
    conversationId: number,
    signal?: AbortSignal,
    answerMessageId?: number,
  ) => Promise<FollowUpSuggestionsResponse>;
}

interface ConversationSuggestions {
  /** The question the answer replied to; a newer question makes these stale. */
  question: string;
  suggestions: string[];
}

const toConversationNumber = (
  conversationId: string | number | null | undefined,
): number | null => {
  const parsed =
    typeof conversationId === "number"
      ? conversationId
      : typeof conversationId === "string"
        ? Number.parseInt(conversationId, 10)
        : NaN;
  return Number.isFinite(parsed) ? parsed : null;
};

const cleanSuggestions = (response: FollowUpSuggestionsResponse): string[] =>
  Array.isArray(response?.suggestions)
    ? response.suggestions.filter(
        (item): item is string =>
          typeof item === "string" && item.trim() !== "",
      )
    : [];

/** The saved id of an answer; a grouped answer's own row is its last one. */
const savedAnswerId = (message: SuggestionMessage): number | undefined =>
  message.hubMessageIds?.[message.hubMessageIds.length - 1] ??
  message.hubMessageId;

const lastUserQuestion = (messages: SuggestionMessage[]): string => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index].role === "user") {
      return (messages[index].content ?? "").trim();
    }
  }
  return "";
};

/**
 * Follow-up question chips for the newest IDA answer.
 *
 * Suggestions are requested only when an answer finishes streaming in this
 * session (a loading -> idle transition), never when an old conversation is
 * opened, so browsing history costs no model calls. They are kept per
 * conversation and shown while that answer is still the newest message:
 * sending anything hides them.
 *
 * The request is tied to the turn, not to the answer's message id, so the id
 * settling from temporary to persisted after the stream ends neither cancels
 * nor repeats it. A new chat receiving its conversation id mid-stream keeps the
 * turn armed; switching to another conversation disarms it.
 */
export function useFollowUpSuggestionChips({
  enabled,
  workspaceId,
  conversationId,
  messages,
  isLoading,
  fetchSuggestions,
}: UseFollowUpSuggestionChipsOptions): string[] {
  const [resultsByConversation, setResultsByConversation] = useState<
    Record<number, ConversationSuggestions>
  >({});
  const armedRef = useRef(false);
  const controllerRef = useRef<AbortController | null>(null);
  const conversationNumber = toConversationNumber(conversationId);
  const previousConversationRef = useRef(conversationNumber);

  const lastMessage = messages[messages.length - 1];
  const answerReady = Boolean(
    lastMessage &&
      lastMessage.role === "assistant" &&
      !lastMessage.isStreaming &&
      (lastMessage.content ?? "").trim(),
  );
  const question = lastUserQuestion(messages);
  const answerMessageId = lastMessage ? savedAnswerId(lastMessage) : undefined;

  useEffect(() => {
    const previous = previousConversationRef.current;
    previousConversationRef.current = conversationNumber;
    // null -> id is a new chat being saved during its first turn.
    if (previous !== null && previous !== conversationNumber) {
      armedRef.current = false;
      controllerRef.current?.abort();
      controllerRef.current = null;
    }
  }, [conversationNumber]);

  useEffect(() => {
    if (!isLoading) return;
    armedRef.current = true;
    controllerRef.current?.abort();
    controllerRef.current = null;
    // A new turn makes this conversation's chips stale even when the question
    // text repeats, so they cannot return if the next request fails.
    // previousConversationRef already holds this render's conversation: the
    // effect that updates it is declared above this one.
    const current = previousConversationRef.current;
    if (current !== null) {
      setResultsByConversation((previous) => {
        if (!(current in previous)) return previous;
        const next = { ...previous };
        delete next[current];
        return next;
      });
    }
  }, [isLoading]);

  useEffect(() => {
    if (
      !enabled ||
      isLoading ||
      !answerReady ||
      conversationNumber === null ||
      !armedRef.current
    ) {
      return;
    }
    armedRef.current = false;
    const controller = new AbortController();
    controllerRef.current = controller;
    const target = conversationNumber;
    const answeredQuestion = question;
    fetchSuggestions(workspaceId, target, controller.signal, answerMessageId)
      .then((response) => {
        if (controller.signal.aborted) return;
        setResultsByConversation((previous) => ({
          ...previous,
          [target]: {
            question: answeredQuestion,
            suggestions: cleanSuggestions(response),
          },
        }));
      })
      .catch(() => {
        // Optional garnish: a failed request simply shows no chips.
      })
      .finally(() => {
        if (controllerRef.current === controller) {
          controllerRef.current = null;
        }
      });
  }, [
    answerMessageId,
    answerReady,
    conversationNumber,
    enabled,
    fetchSuggestions,
    isLoading,
    question,
    workspaceId,
  ]);

  useEffect(() => () => controllerRef.current?.abort(), []);

  if (!enabled || isLoading || !answerReady || conversationNumber === null) {
    return [];
  }
  const result = resultsByConversation[conversationNumber];
  if (!result || result.question !== question) {
    return [];
  }
  return result.suggestions;
}
